import { supabase } from './supabase';
import { Payroll } from '../types';
import { employeeService } from './employeeService';
import { attendanceService } from './attendanceService';
import { deductionService, DeductionCalculation } from './deductionService';
import { holidayService } from './holidayService';
import {
  calculateSSSContribution,
  calculatePhilHealthContribution,
  calculatePagIBIGContribution,
  calculateMonthlyWithholdingTax,
  calculateHoursWorked,
  calculateOvertimePay,
  calculateHourlyRate,
  calculateNetPay,
  calculate13thMonthPay
} from '../utils/philippinePayrollUtils';

export const payrollService = {
  // Get all payroll records
  async getAll(): Promise<Payroll[]> {
    const { data, error } = await supabase
      .from('payrolls')
      .select('*')
      .order('payment_date', { ascending: false });

    if (error) {
      console.error('Error fetching payroll records:', error);
      if (error.code === '42P01') {
        throw new Error('Database tables not found. Please run the setup script first.');
      }
      return [];
    }

    return data as Payroll[];
  },

  // Get payroll by ID
  async getById(id: string): Promise<Payroll | null> {
    const { data, error } = await supabase
      .from('payrolls')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error(`Error fetching payroll with ID ${id}:`, error);
      return null;
    }

    return data as Payroll;
  },

  // Get payroll records by employee ID
  async getByEmployeeId(employeeId: string): Promise<Payroll[]> {
    const { data, error } = await supabase
      .from('payrolls')
      .select('*')
      .eq('employee_id', employeeId)
      .order('payment_date', { ascending: false });

    if (error) {
      console.error(`Error fetching payroll for employee ${employeeId}:`, error);
      return [];
    }

    return data as Payroll[];
  },

  // Get payroll records by date range
  async getByDateRange(startDate: string, endDate: string): Promise<Payroll[]> {
    const { data, error } = await supabase
      .from('payrolls')
      .select('*')
      .gte('payment_date', startDate)
      .lte('payment_date', endDate)
      .order('payment_date', { ascending: false });

    if (error) {
      console.error(`Error fetching payroll for date range ${startDate} to ${endDate}:`, error);
      return [];
    }

    return data as Payroll[];
  },

  // Create a new payroll record
  async create(payroll: Omit<Payroll, 'id' | 'created_at' | 'updated_at'>): Promise<Payroll | null> {
    const { data, error } = await supabase
      .from('payrolls')
      .insert([{ ...payroll }])
      .select()
      .single();

    if (error) {
      console.error('Error creating payroll record:', error);
      return null;
    }

    return data as Payroll;
  },

  // Update a payroll record
  async update(id: string, payroll: Partial<Payroll>): Promise<Payroll | null> {
    const { data, error } = await supabase
      .from('payrolls')
      .update({ ...payroll, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error(`Error updating payroll with ID ${id}:`, error);
      return null;
    }

    return data as Payroll;
  },

  // Delete a payroll record
  async delete(id: string): Promise<boolean> {
    const { error } = await supabase
      .from('payrolls')
      .delete()
      .eq('id', id);

    if (error) {
      console.error(`Error deleting payroll with ID ${id}:`, error);
      return false;
    }

    return true;
  },

  // Generate payroll for an employee for a specific period with Philippine calculations
  async generatePayroll(employeeId: string, startDate: string, endDate: string): Promise<Payroll | null> {
    try {
      // Get employee details
      const employee = await employeeService.getById(employeeId);
      if (!employee) {
        console.error(`Employee with ID ${employeeId} not found`);
        return null;
      }

      // Check if payroll already exists for this employee and pay period
      const { data: existingPayroll, error: checkError } = await supabase
        .from('payrolls')
        .select('id')
        .eq('employee_id', employeeId)
        .eq('pay_period_start', startDate)
        .eq('pay_period_end', endDate)
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        console.error('Error checking for existing payroll:', checkError);
        return null;
      }

      if (existingPayroll) {
        console.warn(`Payroll already exists for employee ${employeeId} for period ${startDate} to ${endDate}`);
        return null;
      }

      // Get attendance records for the employee and period (more efficient)
      const employeeAttendance = await attendanceService.getByEmployeeAndDateRange(employeeId, startDate, endDate);

      // Calculate total hours worked and overtime
      let totalHoursWorked = 0;
      let regularHours = 0;
      let overtimeHours = 0;
      
      console.log(`Calculating payroll for employee ${employeeId} from ${startDate} to ${endDate}`);
      console.log(`Found ${employeeAttendance.length} attendance records`);
      
      if (employeeAttendance.length === 0) {
        console.warn(`No attendance records found for employee ${employeeId} in period ${startDate} to ${endDate}`);
        // Don't generate payroll for employees with no attendance records
        return null;
      }
      
      let validRecords = 0;
      let holidayWorkProcessed = 0;
      
      // Process attendance records and check for holiday work
      for (const record of employeeAttendance) {
        if (record.clock_in && record.clock_out) {
          const hours = calculateHoursWorked(record.clock_in, record.clock_out, record.break_start || undefined, record.break_end || undefined);
          console.log(`Date: ${record.date}, Clock In: ${record.clock_in}, Clock Out: ${record.clock_out}, Hours: ${hours.totalHours}`);
          totalHoursWorked += hours.totalHours;
          regularHours += hours.regularHours;
          overtimeHours += hours.overtimeHours;
          validRecords++;
          
          // Check if this date is a holiday and process holiday work
          const holidayWorkRecord = await holidayService.processHolidayWorkFromAttendance(
            employeeId,
            record.date,
            hours.totalHours,
            employee.salary
          );
          
          if (holidayWorkRecord) {
            holidayWorkProcessed++;
            console.log(`Holiday work processed for ${record.date}: ${holidayWorkRecord.total_pay.toFixed(2)}`);
          }
        } else {
          console.log(`Incomplete attendance record for ${record.date}: clock_in=${record.clock_in}, clock_out=${record.clock_out}`);
        }
      }
      
      console.log(`Total calculated hours - Total: ${totalHoursWorked}, Regular: ${regularHours}, Overtime: ${overtimeHours}`);
      console.log(`Valid attendance records processed: ${validRecords} out of ${employeeAttendance.length}`);
      console.log(`Holiday work records processed: ${holidayWorkProcessed}`);

      // Don't generate payroll for employees with no valid attendance records
      if (validRecords === 0) {
        console.warn(`No valid attendance records (with clock-in and clock-out) found for employee ${employeeId} in period ${startDate} to ${endDate}`);
        return null;
      }

      // Calculate hourly rate if not provided
      const hourlyRate = employee.hourly_rate || calculateHourlyRate(employee.salary);
      
      // Calculate basic salary based on actual hours worked
      const basicSalary = regularHours * hourlyRate;
      
      // Calculate overtime pay using Philippine standards
      const overtimePay = calculateOvertimePay(hourlyRate, overtimeHours);

      // Calculate bonuses (can be enhanced based on business rules)
      const bonuses = 0;
      
      // Check if it's December for 13th month pay calculation
      const payPeriodDate = new Date(endDate);
      const isDecember = payPeriodDate.getMonth() === 11; // December is month 11 (0-indexed)
      
      // Calculate 13th month pay for December
      const thirteenthMonthPay = isDecember ? calculate13thMonthPay(employee.salary) : 0;
      
      // Calculate Philippine government contributions based on employee preferences
      const sssContribution = (employee.deduct_sss !== false) ? calculateSSSContribution(employee.salary) : 0;
      const philhealthContribution = (employee.deduct_philhealth !== false) ? calculatePhilHealthContribution(employee.salary) : 0;
      const pagibigContribution = (employee.deduct_pagibig !== false) ? calculatePagIBIGContribution(employee.salary) : 0;
      
      // Calculate withholding tax based on employee preferences
      const withholdingTax = (employee.deduct_tax !== false) ? calculateMonthlyWithholdingTax(employee.salary) : 0;
      
      // Process existing employee deductions (cash advance, meal, transportation, etc.)
      console.log(`Processing existing deductions for employee ${employeeId}...`);
      const deductionCalculation = await deductionService.calculatePayrollDeductions(employeeId);
      const otherDeductions = deductionCalculation.total_deductions;
      
      console.log(`Employee deductions summary:`);
      console.log(`  - Total other deductions: ₱${otherDeductions.toFixed(2)}`);
      if (deductionCalculation.deductions.length > 0) {
        deductionCalculation.deductions.forEach(deduction => {
          console.log(`    * ${deduction.description}: ₱${deduction.amount.toFixed(2)} (remaining: ₱${deduction.remaining_after.toFixed(2)})`);
        });
      } else {
        console.log(`    * No active deductions found`);
      }
      
      // Get holiday pay for this period
      const holidayPaySummary = await holidayService.getEmployeeHolidayPaySummary(
        employeeId,
        startDate,
        endDate
      );
      
      const holidayPay = holidayPaySummary?.total_holiday_pay || 0;
      const holidayAdditionalPay = holidayPaySummary?.total_additional_pay || 0;
      
      console.log(`Holiday pay calculation:`);
      console.log(`  - Holiday pay: ₱${holidayPay.toFixed(2)}`);
      console.log(`  - Holiday additional pay: ₱${holidayAdditionalPay.toFixed(2)}`);
      
      // Calculate net pay including holiday pay
      const payCalculation = calculateNetPay({
        basicSalary,
        overtimePay,
        bonuses,
        thirteenthMonthPay,
        holidayPay,
        holidayAdditionalPay,
        sssContribution,
        philhealthContribution,
        pagibigContribution,
        withholdingTax,
        otherDeductions
      });

      // Create payroll record
      const payrollData: Omit<Payroll, 'id' | 'created_at' | 'updated_at'> = {
        employee_id: employeeId,
        pay_period_start: startDate,
        pay_period_end: endDate,
        basic_salary: basicSalary,
        overtime_pay: overtimePay,
        bonuses: bonuses,
        thirteenth_month_pay: thirteenthMonthPay,
        holiday_pay: holidayPay,
        holiday_additional_pay: holidayAdditionalPay,
        gross_pay: payCalculation.grossPay,
        sss_contribution: sssContribution,
        philhealth_contribution: philhealthContribution,
        pagibig_contribution: pagibigContribution,
        withholding_tax: withholdingTax,
        other_deductions: otherDeductions,
        total_deductions: payCalculation.totalDeductions,
        net_pay: payCalculation.netPay,
        total_hours_worked: totalHoursWorked,
        regular_hours: regularHours,
        overtime_hours: overtimeHours,
        payment_date: new Date().toISOString().split('T')[0],
        payment_status: 'pending',
        notes: `Payroll for ${employee.first_name} ${employee.last_name} for period ${startDate} to ${endDate}. Hours: ${totalHoursWorked.toFixed(2)} (Regular: ${regularHours.toFixed(2)}, OT: ${overtimeHours.toFixed(2)}). ${holidayWorkProcessed > 0 ? `Holiday work: ${holidayWorkProcessed} days, ₱${(holidayPay + holidayAdditionalPay).toFixed(2)}. ` : ''}${isDecember ? '13th Month Pay included. ' : ''}Other deductions: ₱${otherDeductions.toFixed(2)}. Processed ${validRecords} valid attendance records out of ${employeeAttendance.length}.`
      };

      const createdPayroll = await this.create(payrollData);
      
      // Update deduction balances after successful payroll creation
      if (createdPayroll && deductionCalculation.deductions.length > 0) {
        console.log(`Updating deduction balances for employee ${employeeId}...`);
        await deductionService.updateDeductionBalances([deductionCalculation]);
        console.log(`✅ Deduction balances updated successfully`);
      }

      return createdPayroll;
    } catch (error) {
      console.error('Error generating payroll:', error);
      return null;
    }
  },

  // Helper function to calculate days in a period
  calculateDaysInPeriod(startDate: string, endDate: string): number {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end dates
    return diffDays;
  },

  // Process payment for a payroll record
  async processPayment(payrollId: string): Promise<Payroll | null> {
    try {
      // In a real system, this would integrate with a payment gateway or bank API
      // For this example, we'll just update the status to 'paid'
      return this.update(payrollId, {
        payment_status: 'paid',
        notes: `Payment processed on ${new Date().toISOString()}`
      });
    } catch (error) {
      console.error(`Error processing payment for payroll ${payrollId}:`, error);
      return this.update(payrollId, {
        payment_status: 'failed',
        notes: `Payment failed on ${new Date().toISOString()}: ${error}`
      });
    }
  },

  // Generate payroll report for all employees for a specific period
  async generatePayrollReport(startDate: string, endDate: string): Promise<Payroll[]> {
    try {
      // Get all active employees
      const employees = await employeeService.getAll();
      const activeEmployees = employees.filter(emp => emp.status === 'active');
      
      const payrolls: Payroll[] = [];
      let employeesProcessed = 0;
      let employeesSkippedNoAttendance = 0;
      let employeesSkippedExistingPayroll = 0;
      let totalDeductionsProcessed = 0;
      
      console.log(`Starting payroll generation for ${activeEmployees.length} active employees from ${startDate} to ${endDate}`);
      console.log(`🔍 Pre-processing employee deductions...`);
      
      // Pre-check deductions for all employees
      const employeeIds = activeEmployees.map(emp => emp.id);
      const allDeductionCalculations = await deductionService.getAllEmployeeDeductions(employeeIds);
      const employeesWithDeductions = allDeductionCalculations.filter(calc => calc.total_deductions > 0);
      
      console.log(`📊 Deduction Summary:`);
      console.log(`  - ${employeesWithDeductions.length} employees have active deductions`);
      const totalDeductionAmount = employeesWithDeductions.reduce((sum, calc) => sum + calc.total_deductions, 0);
      console.log(`  - Total deductions to process: ₱${totalDeductionAmount.toFixed(2)}`);
      
      // Generate payroll for each employee
      for (const employee of activeEmployees) {
        // Check if payroll already exists for this employee and pay period
        const { data: existingPayroll, error: checkError } = await supabase
          .from('payrolls')
          .select('id')
          .eq('employee_id', employee.id)
          .eq('pay_period_start', startDate)
          .eq('pay_period_end', endDate)
          .single();

        if (checkError && checkError.code !== 'PGRST116') {
          console.error(`Error checking existing payroll for ${employee.first_name} ${employee.last_name}:`, checkError);
          continue;
        }

        if (existingPayroll) {
          employeesSkippedExistingPayroll++;
          console.log(`⚠️ Skipped ${employee.first_name} ${employee.last_name} - payroll already exists for this period`);
          continue;
        }

        const payroll = await this.generatePayroll(employee.id, startDate, endDate);
        if (payroll) {
          payrolls.push(payroll);
          employeesProcessed++;
          
          // Count deductions processed for this employee
          const employeeDeductions = allDeductionCalculations.find(calc => calc.employee_id === employee.id);
          if (employeeDeductions && employeeDeductions.total_deductions > 0) {
            totalDeductionsProcessed += employeeDeductions.total_deductions;
          }
          
          console.log(`✅ Payroll generated for ${employee.first_name} ${employee.last_name}`);
        } else {
          employeesSkippedNoAttendance++;
          console.log(`⏭️ Skipped ${employee.first_name} ${employee.last_name} - no valid attendance records`);
        }
      }
      
      console.log(`\n🎉 Payroll generation completed:`);
      console.log(`  - ${employeesProcessed} employees processed`);
      console.log(`  - ${employeesSkippedNoAttendance} employees skipped (no attendance records)`);
      console.log(`  - ${employeesSkippedExistingPayroll} employees skipped (payroll already exists)`);
      console.log(`  - ₱${totalDeductionsProcessed.toFixed(2)} total deductions processed`);
      return payrolls;
    } catch (error) {
      console.error('Error generating payroll report:', error);
      return [];
    }
  }
};